import { createServerFn } from "@tanstack/react-start";
import { publicSupabase } from "@/lib/supabase-public";
import { z } from "zod";
import { requireAuth as requireSupabaseAuth } from "@/lib/require-auth";
import type { Database } from "@/integrations/supabase/types";
import type { PageSeoOverride } from "./seo-overrides";

export type PageRow = {
  id: string;
  slug: string;
  kind: string;
  title: string;
  meta_title: string | null;
  meta_description: string | null;
  canonical_url: string | null;
  schema_json: any;
  sections: Record<string, string>;
  sections_accepted: Record<string, string>;
  assigned_images: Record<string, string>;
  humanized_at: string | null;
  updated_at: string;
};

const SECTION_KEYS = ["intro", "main", "climate", "process", "why_us", "faq", "cta"] as const;

const PAGE_SELECT =
  "id, slug, kind, title, meta_title, meta_description, canonical_url, schema_json, sections, sections_accepted, assigned_images, humanized_at, updated_at";

function shapePage(r: any): PageRow {
  return {
    id: r.id,
    slug: r.slug,
    kind: r.kind,
    title: r.title,
    meta_title: r.meta_title,
    meta_description: r.meta_description,
    canonical_url: r.canonical_url,
    schema_json: r.schema_json,
    sections: (r.sections ?? {}) as Record<string, string>,
    sections_accepted: (r.sections_accepted ?? {}) as Record<string, string>,
    assigned_images: (r.assigned_images ?? {}) as Record<string, string>,
    humanized_at: r.humanized_at,
    updated_at: r.updated_at,
  };
}

async function publicClient() {
  const client = await publicSupabase();
  if (!client) throw new Error("Server not configured");
  return client;
}

async function ensureAdmin(supabase: any, userId: string) {
  const { data } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
  if (!data) throw new Error("Forbidden");
}

/* -------- public read for SSR fallback (publishable client, anon-safe) -------- */
export const getPageBySlug = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => z.object({ slug: z.string().min(1) }).parse(input))
  .handler(async ({ data }): Promise<PageRow | null> => {
    const supabase = await publicClient();
    const { data: row } = await supabase
      .from("pages")
      .select(PAGE_SELECT)
      .eq("slug", data.slug)
      .maybeSingle();
    return row ? shapePage(row) : null;
  });

/**
 * Public, anon-safe read of just the SEO override fields for one page.
 * Used by route loaders so head() can prefer admin values over route defaults.
 */
export const getPageSeo = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => z.object({ slug: z.string().min(1) }).parse(input))
  .handler(async ({ data }): Promise<PageSeoOverride | null> => {
    const supabase = await publicClient();
    const { data: row } = await supabase
      .from("pages")
      .select("meta_title, meta_description, canonical_url, schema_json")
      .eq("slug", data.slug)
      .maybeSingle();
    if (!row) return null;
    return {
      metaTitle: row.meta_title ?? null,
      metaDescription: row.meta_description ?? null,
      canonicalUrl: row.canonical_url ?? null,
      schemaJson: row.schema_json ?? null,
    };
  });



/* -------- admin -------- */
export const adminListPages = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<PageRow[]> => {
    await ensureAdmin(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("pages")
      .select(PAGE_SELECT)
      .order("kind")
      .order("slug");
    if (error) throw new Error(error.message);
    return (data ?? []).map(shapePage);
  });

export const adminGetPage = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ slug: z.string() }).parse(input))
  .handler(async ({ data, context }): Promise<PageRow | null> => {
    await ensureAdmin(context.supabase, context.userId);
    const { data: row, error } = await context.supabase
      .from("pages")
      .select(PAGE_SELECT)
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return row ? shapePage(row) : null;
  });

export const adminUpdatePageMeta = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        slug: z.string(),
        title: z.string().optional(),
        meta_title: z.string().nullable().optional(),
        meta_description: z.string().nullable().optional(),
        canonical_url: z.string().nullable().optional(),
        schema_json: z.any().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { slug, ...patch } = data;
    const { error } = await context.supabase.from("pages").update(patch).eq("slug", slug);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const adminUpdatePageSections = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        slug: z.string(),
        sections: z.record(z.string()),
        mark_humanized: z.boolean().optional(),
        accept_sections: z.array(z.string()).optional(),
        unaccept_sections: z.array(z.string()).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);

    // Load current row to compute accepted-state diff.
    const { data: current, error: getErr } = await context.supabase
      .from("pages")
      .select("sections, sections_accepted")
      .eq("slug", data.slug)
      .maybeSingle();
    if (getErr) throw new Error(getErr.message);

    const prevSections = ((current?.sections ?? {}) as Record<string, string>);
    const prevAccepted = ((current?.sections_accepted ?? {}) as Record<string, string>);
    const nextAccepted: Record<string, string> = { ...prevAccepted };
    const now = new Date().toISOString();

    // Auto-clear accepted flag for any section whose text actually changed.
    for (const key of Object.keys(data.sections)) {
      if (prevAccepted[key] && (data.sections[key] ?? "") !== (prevSections[key] ?? "")) {
        delete nextAccepted[key];
      }
    }
    for (const key of data.unaccept_sections ?? []) {
      delete nextAccepted[key];
    }
    for (const key of data.accept_sections ?? []) {
      nextAccepted[key] = now;
    }

    const patch: any = { sections: data.sections, sections_accepted: nextAccepted };
    if (data.mark_humanized) patch.humanized_at = now;
    const { error } = await context.supabase.from("pages").update(patch).eq("slug", data.slug);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const adminUpdatePageImages = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ slug: z.string(), assigned_images: z.record(z.string()) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { error } = await context.supabase
      .from("pages")
      .update({ assigned_images: data.assigned_images })
      .eq("slug", data.slug);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export { SECTION_KEYS };
